import {Component, EventEmitter, inject, Input, OnInit, Output, signal} from '@angular/core';
import {GameTablesService} from '../../services/gametables.service';
import {ActivatedRoute, Router} from '@angular/router';
import {AlertService} from '../../../../shared/services/alert.service';
import {GameTable} from '../../../../shared/model/game-table';
import {ApiResponse} from '../../../../shared/model/api-response';
import {Messages} from '../../../../shared/constants/messages';
import {GameTableFormComponent} from '../game-table-form/game-table-form.component';
import {EventService} from '../../../../shared/services/event.service';
import {Event} from '../../../../shared/model/event';
import {PermissionService} from '../../../../core/services/permission.service';
import {OrgCapability, SystemPower} from '../../../../shared/model/permissions';
import {OrganizationService} from '../../../../shared/services/organization.service';
import {Organization} from '../../../../shared/model/organization';
import {forkJoin, of} from 'rxjs';

@Component({
  selector: 'app-game-table-edit',
  standalone: true,
  imports: [
    GameTableFormComponent
  ],
  templateUrl: './game-table-edit.component.html',
  styleUrl: './game-table-edit.component.css'
})
export class GameTableEditComponent implements OnInit {
  private gameTableService: GameTablesService = inject(GameTablesService);
  private router: Router = inject(Router);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private alertService: AlertService = inject(AlertService);
  private eventService: EventService = inject(EventService);
  private permissionService = inject(PermissionService);
  private organizationService = inject(OrganizationService);

  /** Modalità embedded (es. modale admin): niente route né navigazione. */
  @Input() embeddedId?: number;
  @Output() saved = new EventEmitter<GameTable | undefined>();
  @Output() cancelled = new EventEmitter<void>();

  protected tableId?: number;
  protected initialData = signal<GameTable | undefined>(undefined);
  protected availableEvents = signal<Array<Event>>([]);
  protected availableOrganizations = signal<Organization[]>([]);
  protected isLoading = signal<boolean>(true);

  ngOnInit(): void {
    const rawId = this.embeddedId ?? this.route.snapshot.paramMap.get('id');
    if (!rawId) {
      this.handleLoadError();
      return;
    }
    this.tableId = Number(rawId);
    this.loadAllData(this.tableId);
  }

  private loadAllData(id: number): void {
    this.isLoading.set(true);

    const orgs$ = this.permissionService.hasSystemPower(SystemPower.MODERATOR)
      ? this.organizationService.getAllOrganizations()
      : of({
          success: true,
          data: this.permissionService.organizationsWithCapability(OrgCapability.ORG_TABLE_CREATE),
        } as ApiResponse<Organization[]>);

    const events$ = this.permissionService.hasSystemPower(SystemPower.MODERATOR)
      ? this.eventService.getAllEvents()
      : this.eventService.getMyEvents();

    const table$ = this.gameTableService.getTableById(id);

    forkJoin({
      table: table$,
      orgs: orgs$,
      events: events$,
    }).subscribe({
      next: ({ table: tableResp, orgs: orgsResp, events: eventsResp }) => {
        if (!tableResp.success || !tableResp.data) {
          this.handleLoadError();
          return;
        }

        const table = tableResp.data;
        const orgs = orgsResp.success && orgsResp.data ? orgsResp.data.filter((org) => org.organizationActive) : [];
        const events = eventsResp.success && eventsResp.data ? eventsResp.data : [];

        const mergedOrgs = this.mergeTableOrganization(table, orgs);

        this.availableOrganizations.set(mergedOrgs);
        this.availableEvents.set(events);
        this.initialData.set(table);
        this.isLoading.set(false);
      },
      error: () => {
        this.handleLoadError();
      },
    });
  }

  onSubmit(tableData: Partial<GameTable>) {
    if (this.tableId) {
      const updatedTable: Partial<GameTable> = {
        ...tableData,
        id: this.tableId
      };

      this.gameTableService.updateTable(updatedTable).subscribe({
        next: (response: ApiResponse<GameTable>) => {
          if (response.success) {
            this.alertService.showSuccess(Messages.TABLE_UPDATED_SUCCESS);
            if (this.embeddedId) {
              this.saved.emit(response.data ?? undefined);
              return;
            }
            this.router.navigate(['/tables/detail', this.tableId]);
          }
        }
      });
    }
  }

  onCancel() {
    if (this.embeddedId) {
      this.cancelled.emit();
      return;
    }
    if (this.tableId) {
      this.router.navigate(['/tables/detail', this.tableId]);
    } else {
      this.router.navigate(['/tables']);
    }
  }

  private mergeTableOrganization(table: GameTable, currentOrgs: Organization[]): Organization[] {
    const organization = table.organization ?? table.event?.organization;
    if (!organization || currentOrgs.some((item) => item.id === organization.id)) {
      return currentOrgs;
    }
    return [
      ...currentOrgs,
      {
        id: organization.id,
        organizationName: organization.organizationName,
        organizationLogo: organization.organizationLogo,
        organizationActive: true,
        member: true,
        moderator: false,
        admin: false,
        owner: false
      }
    ];
  }

  private handleLoadError(): void {
    this.isLoading.set(false);
    this.alertService.showError(Messages.TABLE_LOAD_ERROR);
    if (this.embeddedId) {
      this.cancelled.emit();
      return;
    }
    this.router.navigate(['/tables']);
  }
}
